import { computed, inject, Injectable, signal } from "@angular/core";
import {
  collection,
  collectionData,
  CollectionReference,
  doc,
  docData,
  DocumentReference,
  Firestore,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { EMPTY, from, Observable, of } from "rxjs"; // Added combineLatest
import { catchError, map, switchMap, take } from "rxjs/operators";
import { PathConstants } from "../../db.paths";
import { Case, CaseStatus, Metric } from "../models/case.model"; // Removed CaseIntakeForm
import { AuthService } from "./auth.service";
import { DocumentService } from "./document.service"; // Import DocumentService
import { NotesService } from "./notes.service"; // Import NotesService

@Injectable({
  providedIn: "root",
})
export class CaseService {
  private firestore: Firestore = inject(Firestore);
  private authService: AuthService = inject(AuthService);
  private functions: Functions = inject(Functions);
  private documentService: DocumentService = inject(DocumentService); // Inject DocumentService
  private notesService: NotesService = inject(NotesService); // Inject NotesService

  // Filtering and sorting signals
  private defaultMetric: Metric = {
    title: "All Cases",
    count: 0,
    filterValue: "",
    icon: "dashboard",
  };
  activeFilter = signal<Metric>(this.defaultMetric);
  activeFilterDetails = computed(() => this.activeFilter());

  constructor() {}

  setFilter(metric: Metric): void {
    this.activeFilter.set(metric);
  }

  getFilteredCases(
    filter: Metric,
    searchTerm: string,
    sortColumn: string,
    sortDirection: "asc" | "desc",
  ): Observable<{ cases: Case[]; totalItems: number }> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          return of({ cases: [], totalItems: 0 });
        }

        const casesPath = PathConstants.getCasesPath(user.uid);
        let casesCollectionRef: CollectionReference<Case> = collection(
          this.firestore,
          casesPath,
        ) as CollectionReference<Case>;

        // Build query constraints array
        const constraints: any[] = [];

        // Apply status filter
        if (filter.filterValue) {
          let statusArray: CaseStatus[] = [];
          const lowerCaseFilterValue = filter.filterValue.toLowerCase();
          console.log(
            "[CaseService] getFilteredCases - lowerCaseFilterValue:",
            lowerCaseFilterValue,
          );

          if (filter.filterValue === "Submitted") {
            statusArray = ["pending", "on-hold", "rejected", "new"];
          } else if (
            lowerCaseFilterValue === "pending" ||
            lowerCaseFilterValue === "new" ||
            lowerCaseFilterValue === "on-hold" ||
            lowerCaseFilterValue === "rejected"
          ) {
            statusArray = ["pending", "new", "on-hold", "rejected"];
          } else if (
            lowerCaseFilterValue === "active" ||
            lowerCaseFilterValue === "accepted"
          ) {
            statusArray = ["active", "accepted"];
          } else {
            statusArray = [lowerCaseFilterValue as CaseStatus];
          }
          console.log(
            "[CaseService] getFilteredCases - filter.filterValue:",
            filter.filterValue,
          );
          console.log(
            "[CaseService] getFilteredCases - statusArray:",
            statusArray,
          );
          constraints.push(where("status", "in", statusArray));
        }

        // Apply search term
        if (searchTerm) {
          const start = searchTerm.toUpperCase().trim();
          const end = start + "\uf8ff";
          constraints.push(where("firstName", ">=", start));
          constraints.push(where("firstName", "<=", end));
        }

        // Apply sorting - must come after where clauses
        // Note: Firestore requires composite indices when combining where + orderBy on different fields
        // Map UI column names to Firestore field names
        const fieldMap: { [key: string]: string } = {
          patientName: "firstName", // Sort by firstName since patientName is firstName + lastName
          createdOn: "createdOn",
        };

        // Use requested sort or default to createdOn descending
        const columnToSort = sortColumn || "createdOn";
        const directionToSort = sortColumn ? sortDirection : "desc";
        const firestoreField = fieldMap[columnToSort] || columnToSort;

        // Always apply a valid sort
        constraints.push(orderBy(firestoreField, directionToSort));

        // Build the query with all constraints at once
        const q = query(casesCollectionRef, ...constraints);

        return collectionData<Case>(q, { idField: "id" }).pipe(
          map((cases) => {
            const convertedCases = cases.map(this._convertTimestampsToDates);
            return { cases: convertedCases, totalItems: convertedCases.length };
          }),
          catchError((error) => {
            console.error("[CaseService] Error in getFilteredCases:", error);
            // Log the full error for debugging
            if (error.message && error.message.includes("index")) {
              console.error(
                "[CaseService] Composite index required. Please create the index in Firebase Console.",
              );
            }
            return of({ cases: [], totalItems: 0 });
          }),
        );
      }),
    );
  }

  // TODO: Use count method for the collection instead of fetching all documents and counting them
  private getCaseCountByStatus(status: string | string[]): Observable<number> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          return of(0);
        }
        const casesPath = PathConstants.getCasesPath(user.uid);
        const casesCollectionRef = collection(
          this.firestore,
          casesPath,
        ) as CollectionReference<Case>;
        const statusArray = Array.isArray(status) ? status : [status];
        const q = query(casesCollectionRef, where("status", "in", statusArray));
        return collectionData<Case>(q).pipe(map((cases) => cases.length));
      }),
    );
  }

  /*
  // This method has performance issue as it takes more than 2 seconds to complete

  private getCaseCountByStatus(status: string | string[]): Observable<number> {
    return this.authService.getLoggedInUser().pipe(
      switchMap(user => {
        if (!user) {
          return of(0);
        }
        const casesPath = PathConstants.getCasesPath(user.uid);
        const casesCollectionRef = collection(this.firestore, casesPath) as CollectionReference<Case>;
        const statusArray = Array.isArray(status) ? status : [status];
        const q = query(
          casesCollectionRef,
          where('status', 'in', statusArray)
        );
        return from(getCountFromServer(q)).pipe(
          map(snapshot => snapshot.data().count)
        );
      })
    );
  }

  */

  getDraftCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("draft");
  }

  getPendingCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("pending");
  }

  getacceptedCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("accepted");
  }

  getOnHoldCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("on-hold");
  }

  getRejectedCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("rejected");
  }

  getActiveCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("active");
  }

  getCompletedCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("completed");
  }

  getCancelledCaseCount(): Observable<number> {
    return this.getCaseCountByStatus("cancelled");
  }

  createCase(caseData: any): Promise<DocumentReference> {
    return new Promise((resolve, reject) => {
      this.authService
        .getLoggedInUser()
        .pipe(take(1))
        .subscribe(
          (user) => {
            if (!user) {
              return reject(new Error("User not logged in"));
            }

            const casesPath = PathConstants.getCasesPath(user.uid);
            const casesCollectionRef = collection(
              this.firestore,
              casesPath,
            ) as CollectionReference<Case>;

            // Create a doc ref first to get the generated id
            const docRef = doc(casesCollectionRef);

            const nowIso = new Date().toISOString();
            const newCase: Partial<Case> = {
              ...caseData,
              orgCode: user.defaults?.orgCode,
              createdOn: nowIso,
              updatedOn: nowIso,
              status: "draft",
              patientKey: docRef.id, // <-- use doc id as patientKey
            };

            // Create the case document (with caseNotes field)
            setDoc(docRef, newCase)
              .then(() => {
                // If caseNotes exists and is not empty, also add it to notes sub-collection
                if (caseData.caseNotes && caseData.caseNotes.trim()) {
                  return this.notesService.addNote(
                    docRef.id,
                    caseData.caseNotes,
                  );
                }
                return Promise.resolve();
              })
              .then(() => resolve(docRef))
              .catch((err) => reject(err));
          },
          (err) => reject(err), // subscribe error handler
        );
    });
  }

  updateCase(caseId: string, caseData: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .getLoggedInUser()
        .pipe(take(1))
        .subscribe(
          (user) => {
            if (!user) {
              return reject(new Error("User not logged in"));
            }

            const casesPath = PathConstants.getCasesPath(user.uid);
            const caseDocRef = doc(this.firestore, `${casesPath}/${caseId}`);
            
            const updatedCaseData = {
              ...caseData,
              updatedOn: new Date().toISOString()
            };

            updateDoc(caseDocRef, updatedCaseData)
              .then(() => {
                if (caseData.caseNotes && caseData.caseNotes.trim()) {
                  // Updating notes in the notes sub-collection if it's updated in the form
                  return this.notesService.addNote(
                    caseId,
                    caseData.caseNotes,
                  );
                }
                return Promise.resolve();
              })
              .then(() => resolve())
              .catch((err) => reject(err));
          },
          (err) => reject(err)
        );
    });
  }

  submitDraftCase(caseId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .getLoggedInUser()
        .pipe(
          take(1),
          switchMap((user) => {
            if (!user) {
              throw new Error("User not logged in");
            }
            // First, get the full case details
            return this.getCaseById(caseId).pipe(
              take(1),
              switchMap((caseDetails) => {
                if (!caseDetails) {
                  throw new Error("Case not found");
                }

                const submitReferralCallable = httpsCallable(
                  this.functions,
                  "submitReferral",
                );
                const flattenedCaseDetails = this._flattenObject(caseDetails); // Flatten the caseDetails
                console.log(
                  "[CaseService] Flattened Case Details:",
                  flattenedCaseDetails,
                ); // Added log

                const payload = {
                  uid: user.uid, // Changed from uid to userId for consistency
                  caseId: caseId,
                  ...flattenedCaseDetails, // Spread the flattened object into the payload
                };
                console.log(
                  "[CaseService] Calling submitReferral with payload:",
                  payload,
                );

                return from(submitReferralCallable(payload)).pipe(
                  switchMap(() => {
                    // After successful Cloud Function call, update Firestore status
                    const casePath = `${PathConstants.getCasesPath(user.uid)}/${caseId}`;
                    const caseDocRef = doc(this.firestore, casePath);
                    return from(
                      updateDoc(caseDocRef, {
                        status: "pending",
                        pendingSince: new Date(),
                      }),
                    );
                  }),
                );
              }),
            );
          }),
        )
        .subscribe({
          next: () => resolve(),
          error: (err) => reject(err),
        });
    });
  }

  // Helper function to flatten an object
  private _flattenObject(
    obj: any,
    prefix: string = "",
  ): { [key: string]: any } {
    return Object.keys(obj).reduce((acc, k) => {
      const newKey = prefix ? `${prefix}_${k}` : k;
      if (
        typeof obj[k] === "object" &&
        obj[k] !== null &&
        !Array.isArray(obj[k]) &&
        !(obj[k] instanceof Date)
      ) {
        Object.assign(acc, this._flattenObject(obj[k], newKey));
      } else {
        acc[newKey] = obj[k];
      }
      return acc;
    }, {});
  }

  private _convertTimestampsToDates(caseData: any): Case {
    if (!caseData) return caseData;

    const convertedCase: Case = { ...caseData };

    // List of properties that are dates/timestamps
    const dateFields = [
      "createdOn",
      "updatedOn",
      "dob",
      "doi",
      "doiEnd",
      "rejectedOn",
      "onHoldSince",
      "pendingSince",
      "acceptedOn",
    ];

    dateFields.forEach((field) => {
      if (
        convertedCase[field] &&
        typeof convertedCase[field].toDate === "function"
      ) {
        convertedCase[field] = convertedCase[field].toDate();
      }
    });

    return convertedCase;
  }

  getCaseById(caseId: string): Observable<Case> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          return of(null as any);
        }
        const casePath = `${PathConstants.getCasesPath(user.uid)}/${caseId}`;
        const caseDocRef = doc(
          this.firestore,
          casePath,
        ) as DocumentReference<Case>;
        return docData<Case>(caseDocRef, { idField: "id" }).pipe(
          map(this._convertTimestampsToDates),
        );
      }),
    );
  }

  getCasesByStatus(status: string | string[]): Observable<Case[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          return of([]);
        }
        const casesPath = PathConstants.getCasesPath(user.uid);
        const casesCollectionRef = collection(
          this.firestore,
          casesPath,
        ) as CollectionReference<Case>;
        const statusArray = Array.isArray(status) ? status : [status];
        const q = query(casesCollectionRef, where("status", "in", statusArray));
        return collectionData<Case>(q, { idField: "id" }).pipe(
          map((cases) => cases.map(this._convertTimestampsToDates)),
        );
      }),
    );
  }

  searchCases(searchTerm: string): Observable<Case[]> {
    searchTerm = (searchTerm || "").trim().toLowerCase();
    if (!searchTerm) return of([]);

    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) return of([]);

        const casesPath = PathConstants.getCasesPath(user.uid);
        const casesCollectionRef = collection(
          this.firestore,
          casesPath,
        ) as CollectionReference<Case>;

        // Read entire collection once and filter client-side
        return collectionData<Case>(casesCollectionRef, { idField: "id" }).pipe(
          map((list) => {
            const terms = searchTerm.split(/\s+/).filter((t) => t.length);
            return list
              .filter((c) => {
                const fn = (c.firstName || "").toLowerCase();
                // fallback for different capitalization in your DB
                const ln = (
                  c.lastName ||
                  (c as any).LastName ||
                  ""
                ).toLowerCase();
                if (terms.length === 1) {
                  const t = terms[0];
                  return fn.includes(t) || ln.includes(t);
                }
                // for multi-term (e.g. "john smith"), require all terms present across first+last
                const combined = `${fn} ${ln}`;
                return terms.every((t) => combined.includes(t));
              })
              .map(this._convertTimestampsToDates);
          }),
        );
      }),
    );
  }

  refreshCase(caseID: string, orgCode: string): Observable<any | null> {
    return this.authService.getLoggedInUser().pipe(
      take(1),
      switchMap((user: any | null) => {
        if (!user) return of(null);

        const callable = httpsCallable(this.functions, "getPatientDetails");
        const payload = { providerOrgCode: orgCode, patientId: caseID };

        return from(callable(payload)).pipe(
          map((res: any) => res?.data ?? null),
          switchMap((patientData: any | null) => {
            if (!patientData) {
              console.warn("No patient details returned from callable.");
              return of(null);
            }

            // Helper for safe date conversion
            const safeDate = (val: any) => {
              if (!val) return null;
              const d = new Date(val);
              return isNaN(d.getTime()) ? null : d;
            };

            // Directly mutate patientData before saving
            if (patientData.dob) patientData.dob = safeDate(patientData.dob);
            if (patientData.doi) patientData.doi = safeDate(patientData.doi);
            patientData.doiEnd = patientData.ct
              ? safeDate(patientData.doiEnd)
              : null;

            // Remove filestore before saving (handled separately)
            const files = { ...patientData.filestore };
            // TODO : check if we still need to delete filestore before saving to Firestores
            // delete patientData.filestore;
            // TODO: Confirm if we still need to delete status before saving to Firestore and change the status to referrence status in future implementations
            delete patientData.status;
            // delete the filestore collection object from the main patient data object since the files are handled separately and updated to caseId/documents collection
            delete patientData.filestore;

            // delete the mutated createdOn date field upon receiving update not to overwrite in case-vue side cases collection
            delete patientData.createdOn;

            // Add server timestamp
            patientData.updatedOn = serverTimestamp();

            const caseDocRef = doc(
              this.firestore,
              PathConstants.getCasesPath(user.uid),
              caseID,
            );
            console.log(
              "[CaseService] refreshCase(): patientData =",
              patientData,
            );
            return from(setDoc(caseDocRef, patientData, { merge: true })).pipe(
              switchMap(() => {
                if (files && Object.keys(files).length > 0) {
                  return this.saveFilesToFirestore(user.uid, caseID, files);
                }
                return of(void 0); // Just continue
              }),
              switchMap(() => this.getCaseById(caseID)), // Refetch the case
              catchError((error) => {
                console.error(
                  "Error saving or refetching case details for caseId:",
                  caseID,
                  error,
                );
                return of(null);
              }),
            );
          }),
          catchError((error) => {
            console.error(
              "Error calling getPatientDetails function for caseId:",
              caseID,
              error,
            );
            return of(null);
          }),
        );
      }),
    );
  }

  // Method to save files to Firestore
  private saveFilesToFirestore(
    userUid: string,
    caseID: string,
    updatedFiles: any,
  ): Observable<void> {
    const fileStorePath = PathConstants.getFileCollectionPath(userUid, caseID);
    const batch = writeBatch(this.firestore);

    return from(getDocs(collection(this.firestore, fileStorePath))).pipe(
      switchMap((existingDocs) => {
        const existingFiles: Record<string, any> = {};

        // Map existing files by their document ID
        existingDocs.forEach((doc) => {
          existingFiles[doc.id] = doc.data();
        });

        // Iterate over updated files
        Object.entries(updatedFiles || {}).forEach(([fileId, fileData]) => {
          const existingFileData = existingFiles[fileId];

          // Add or update only if the file data has changed
          if (
            !existingFileData ||
            JSON.stringify(existingFileData) !== JSON.stringify(fileData)
          ) {
            const fileDocRef = doc(this.firestore, fileStorePath, fileId);
            batch.set(fileDocRef, fileData);
          }
        });

        return from(batch.commit());
      }),
      catchError((error) => {
        console.error("Error saving patient files to Firestore:", error);
        return EMPTY;
      }),
    );
  }

  completeCase(caseId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .getLoggedInUser()
        .pipe(take(1))
        .subscribe((user) => {
          if (!user) {
            return reject("User not logged in");
          }
          const casePath = `${PathConstants.getCasesPath(user.uid)}/${caseId}`;
          const caseDocRef = doc(this.firestore, casePath);
          updateDoc(caseDocRef, { status: "completed" })
            .then(resolve)
            .catch(reject);
        });
    });
  }

  cancelCase(caseId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .getLoggedInUser()
        .pipe(take(1))
        .subscribe((user) => {
          if (!user) {
            return reject("User not logged in");
          }
          const casePath = `${PathConstants.getCasesPath(user.uid)}/${caseId}`;
          const caseDocRef = doc(this.firestore, casePath);
          updateDoc(caseDocRef, { status: "cancelled" })
            .then(resolve)
            .catch(reject);
        });
    });
  }

  moveToDraft(caseId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .getLoggedInUser()
        .pipe(take(1))
        .subscribe((user) => {
          if (!user) {
            return reject("User not logged in");
          }
          const casePath = `${PathConstants.getCasesPath(user.uid)}/${caseId}`;
          const caseDocRef = doc(this.firestore, casePath);
          updateDoc(caseDocRef, { status: "draft" })
            .then(resolve)
            .catch(reject);
        });
    });
  }
}
